import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import type { DocumentMetadata, DocumentMetadataOption } from '@/types/workflow';
import { metadataApi, type MetadataOption } from '@/services/api';

interface DocumentMetadataModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (metadata: DocumentMetadata) => void;
}

// Map backend response (id/name) to frontend format (value/label)
const mapToOption = (option: MetadataOption): DocumentMetadataOption => ({
  value: option.id,
  label: option.name
});

export default function DocumentMetadataModal({ open, onClose, onSave }: DocumentMetadataModalProps) {
  const [validationError, setValidationError] = useState('');
  
  const [proFormaOptions, setProFormaOptions] = useState<DocumentMetadataOption[]>([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<DocumentMetadataOption[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<DocumentMetadataOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedProForma, setSelectedProForma] = useState<DocumentMetadataOption | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentMetadataOption | null>(null);
  const [selectedTerritory, setSelectedTerritory] = useState<DocumentMetadataOption | null>(null);

  useEffect(() => {
    if (open) {
      const fetchOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const [proFormaData, documentTypeData, territoryData] = await Promise.all([
            metadataApi.getProFormaOptions(),
            metadataApi.getDocumentTypeOptions(),
            metadataApi.getTerritoryOptions()
          ]);
          
          setProFormaOptions(proFormaData.map(mapToOption));
          setDocumentTypeOptions(documentTypeData.map(mapToOption));
          setTerritoryOptions(territoryData.map(mapToOption));
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch metadata options');
          console.error('Error fetching metadata options:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();
    }
  }, [open]);

  const handleSave = () => {
    setValidationError('');

    if (!selectedProForma || !selectedDocumentType || !selectedTerritory) {
      setValidationError('All fields are required');
      return;
    }

    const metadata: DocumentMetadata = {
      id: `doc-${crypto.randomUUID()}`,
      documentType: selectedProForma.value,
      documentTypeLabel: selectedProForma.label,
      category: selectedDocumentType.value,
      categoryLabel: selectedDocumentType.label,
      status: selectedTerritory.value,
      statusLabel: selectedTerritory.label
    };

    onSave(metadata);
    setSelectedProForma(null);
    setSelectedDocumentType(null);
    setSelectedTerritory(null);
  };

  const handleClose = () => {
    setSelectedProForma(null);
    setSelectedDocumentType(null);
    setSelectedTerritory(null);
    setValidationError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Document Metadata
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {validationError && <Alert severity="error">{validationError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth>
                <Autocomplete
                  options={proFormaOptions}
                  value={selectedProForma}
                  onChange={(_, newValue) => setSelectedProForma(newValue)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pro Forma"
                      variant="filled"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Autocomplete
                  options={documentTypeOptions}
                  value={selectedDocumentType}
                  onChange={(_, newValue) => setSelectedDocumentType(newValue)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Document Type"
                      variant="filled"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Autocomplete
                  options={territoryOptions}
                  value={selectedTerritory}
                  onChange={(_, newValue) => setSelectedTerritory(newValue)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Territory"
                      variant="filled"
                    />
                  )}
                />
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
